import React from "react";
import {
  Modal,
  Text,
  Stack,
  Paper,
  Group,
  Button,
  Box,
  Table,
  Badge,
} from "@mantine/core";
import { TradeCategoryRule } from "./index";

interface RulesViewModalProps {
  opened: boolean;
  onClose: () => void;
  category: TradeCategoryRule | null;
}

export const RulesViewModal = ({
  opened,
  onClose,
  category,
}: RulesViewModalProps) => {
  if (!category) return null;

  const isUpgrade = category.type === "LÊN ĐỜI" || category.type === "LEN DOI" || category.categoryType === "upgrade";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Quy tắc định giá chi tiết: {category.groupName}
        </Text>
      }
      size="75%"
      centered
      styles={{
        content: {
          maxWidth: "1100px",
          minWidth: "700px",
        },
      }}
    >
      <Stack gap="md">
        {isUpgrade ? (
          (() => {
            const upHead = category.headers.find((h) => h.option === 2);
            if (!upHead) {
              return (
                <Text size="sm" c="gray.5" fs="italic">
                  Chưa cấu hình quy tắc lên đời cho ngành hàng này.
                </Text>
              );
            }
            return (
              <Paper p="md" withBorder radius="md">
                <Stack gap="xs">
                  <Group>
                    <Text fw={600} size="sm">Tỷ lệ hỗ trợ lên đời:</Text>
                    <Text
                      size="sm"
                      style={{
                        color: "var(--mantine-color-blue-7)",
                        fontWeight: 700,
                      }}
                    >
                      +{upHead.percentSupport}%
                    </Text>
                  </Group>
                  <Group>
                    <Text fw={600} size="sm">Giới hạn hỗ trợ tối đa:</Text>
                    <Text size="sm" style={{ fontWeight: 700 }}>
                      {upHead.amountSupport?.toLocaleString()}đ
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            );
          })()
        ) : (
          (() => {
            const buybacks = category.headers.filter((h) => h.option === 1);
            if (buybacks.length === 0) {
              return (
                <Text size="sm" c="gray.5" fs="italic">
                  Chưa cấu hình phân loại thu mua cho ngành hàng này.
                </Text>
              );
            }
            return (
              <Box
                style={{
                  maxHeight: "500px",
                  overflowY: "auto",
                  border: "1px solid var(--mantine-color-gray-2)",
                  borderRadius: "8px",
                }}
              >
                <Table
                  striped
                  highlightOnHover
                  withColumnBorders
                  verticalSpacing="sm"
                  horizontalSpacing="md"
                  style={{ fontSize: "13px" }}
                >
                  <Table.Thead
                    style={{ backgroundColor: "var(--mantine-color-gray-0)" }}
                  >
                    <Table.Tr>
                      <Table.Th style={{ width: "120px" }}>
                        <Text fw={600} size="sm">Phân loại</Text>
                      </Table.Th>
                      <Table.Th style={{ width: "110px" }}>
                        <Text fw={600} size="sm">Tỷ lệ hỗ trợ</Text>
                      </Table.Th>
                      <Table.Th style={{ width: "220px" }}>
                        <Text fw={600} size="sm">Mô tả tình trạng</Text>
                      </Table.Th>
                      <Table.Th style={{ width: "180px" }}>
                        <Text fw={600} size="sm">Phụ kiện đi kèm</Text>
                      </Table.Th>
                      <Table.Th style={{ width: "180px" }}>
                        <Text fw={600} size="sm">Khấu trừ / Ghi chú</Text>
                      </Table.Th>
                      <Table.Th>
                        <Text fw={600} size="sm">Tiêu chí kiểm lỗi</Text>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {buybacks.map((h, idx) => (
                      <Table.Tr key={idx} style={{ verticalAlign: "top" }}>
                        <Table.Td>
                          <Text fw={600} size="sm" c="blue.8">
                            {h.productCondition}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="blue" variant="light" size="md">
                            {h.percentSupport}%
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="xs"
                            style={{
                              whiteSpace: "pre-line",
                              lineHeight: "1.4",
                              color: "var(--mantine-color-gray-7)",
                            }}
                          >
                            {h.shortDescription || (
                              <span
                                style={{
                                  color: "var(--mantine-color-gray-4)",
                                  fontStyle: "italic",
                                }}
                              >
                                Không có mô tả
                              </span>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="xs"
                            style={{
                              whiteSpace: "pre-line",
                              lineHeight: "1.4",
                              color: "var(--mantine-color-gray-7)",
                            }}
                          >
                            {h.includedAccessories || (
                              <span
                                style={{
                                  color: "var(--mantine-color-gray-4)",
                                  fontStyle: "italic",
                                }}
                              >
                                Không yêu cầu
                              </span>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="xs"
                            style={{
                              whiteSpace: "pre-line",
                              lineHeight: "1.4",
                              color: "var(--mantine-color-gray-7)",
                            }}
                          >
                            {h.missingAccessoryNote || (
                              <span
                                style={{
                                  color: "var(--mantine-color-gray-4)",
                                  fontStyle: "italic",
                                }}
                              >
                                Không có ghi chú
                              </span>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {h.tblTradeInLines && h.tblTradeInLines.length > 0 ? (
                            <Stack gap={4}>
                              {h.tblTradeInLines.map((line, lIdx) => (
                                <Group
                                  key={lIdx}
                                  gap={4}
                                  wrap="nowrap"
                                  align="flex-start"
                                >
                                  <Text size="xs" c="red.6" style={{ marginTop: 2 }}>
                                    •
                                  </Text>
                                  <Text size="xs" c="gray.7">
                                    {line.text}
                                  </Text>
                                </Group>
                              ))}
                            </Stack>
                          ) : (
                            <Text size="xs" c="gray.4" fs="italic">
                              Không có tiêu chí
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            );
          })()
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Đóng
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
